import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users, 
  CreditCard, 
  Gift, 
  Trophy,
  TrendingUp,
  Loader2,
  Leaf,
  Drumstick,
  Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [referrals, setReferrals] = useState({ referrals: [], top_referrers: [] });
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, subsRes, refsRes, winnersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/subscriptions`, { withCredentials: true }),
        axios.get(`${API}/admin/referrals`, { withCredentials: true }),
        axios.get(`${API}/admin/puzzle-winners`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setSubscriptions(subsRes.data);
      setReferrals(refsRes.data);
      setWinners(winnersRes.data);
    } catch (error) {
      // Admin data fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) {
      if (!user || user.role !== 'admin') {
        navigate('/');
      } else {
        fetchData();
      }
    }
  }, [user, authLoading, navigate, fetchData]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#E05D3A]" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0]" data-testid="admin-page">
      {/* Header */}
      <div className="bg-white border-b border-[#2C402E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2C402E] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-bold text-[#2C402E]" data-testid="admin-title">Admin Dashboard</h1>
              <p className="text-sm text-[#4A4A4A]">ANAN's Kitchen Management</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="stat-card" data-testid="stat-users">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#E05D3A]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#E05D3A]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-subscriptions">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.active_subscriptions || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-referrals">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Referrals</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_referrals || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-[#D4AF37]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-revenue">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-3xl font-bold text-slate-900">£{stats?.total_revenue || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="subscriptions" className="space-y-6">
          <TabsList className="bg-white border border-[#2C402E]/10 p-1">
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-[#2C402E] data-[state=active]:text-white">
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-[#2C402E] data-[state=active]:text-white">
              Referrals
            </TabsTrigger>
            <TabsTrigger value="winners" className="data-[state=active]:bg-[#2C402E] data-[state=active]:text-white">
              Puzzle Winners
            </TabsTrigger>
          </TabsList>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card data-testid="subscriptions-table">
              <CardHeader>
                <CardTitle>All Subscriptions</CardTitle>
              </CardHeader>
              <CardContent>
                {subscriptions.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No subscriptions yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Customer</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Meal Type</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscriptions.map((sub, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{sub.user_name}</p>
                                <p className="text-sm text-slate-500">{sub.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{sub.user_phone}</TableCell>
                            <TableCell>
                              <Badge className={sub.meal_type === 'veg' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {sub.meal_type === 'veg' ? (
                                  <><Leaf className="w-3 h-3 mr-1" /> Veg</>
                                ) : (
                                  <><Drumstick className="w-3 h-3 mr-1" /> Non-Veg</>
                                )}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <span className="font-medium">£{sub.final_price}</span>
                                {sub.discount > 0 && (
                                  <span className="text-green-600 text-sm ml-1">
                                    (-£{sub.discount})
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-green-100 text-green-700">{sub.status}</Badge>
                            </TableCell>
                            <TableCell>{formatDate(sub.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top Referrers */}
              <Card data-testid="top-referrers">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Top Referrers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {referrals.top_referrers?.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No referrers yet</p>
                  ) : (
                    <div className="space-y-3">
                      {referrals.top_referrers?.map((ref, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                              index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                            }`}>
                              {index + 1}
                            </div>
                            <span className="font-medium">{ref.name}</span>
                          </div>
                          <Badge variant="secondary">{ref.count} referrals</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* All Referrals */}
              <Card className="lg:col-span-2" data-testid="referrals-table">
                <CardHeader>
                  <CardTitle>All Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  {referrals.referrals?.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No referrals yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Referrer</TableHead>
                            <TableHead>Referred</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {referrals.referrals?.map((ref, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{ref.referrer_name}</TableCell>
                              <TableCell>{ref.referred_name}</TableCell>
                              <TableCell>
                                <Badge className="bg-green-100 text-green-700">{ref.status}</Badge>
                              </TableCell>
                              <TableCell>{formatDate(ref.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Winners Tab */}
          <TabsContent value="winners">
            <Card data-testid="winners-table">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Puzzle Winners
                </CardTitle>
              </CardHeader>
              <CardContent>
                {winners.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No winners yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Winner</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Month</TableHead>
                          <TableHead>Prize</TableHead>
                          <TableHead>Won At</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {winners.map((winner, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{winner.user_name}</TableCell>
                            <TableCell>{winner.user_email}</TableCell>
                            <TableCell>
                              {new Date(winner.year, winner.month - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-amber-100 text-amber-700">{winner.prize}</Badge>
                            </TableCell>
                            <TableCell>{formatDate(winner.won_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
